import React, { useState } from "react";
import Avatar from "../common/Avatar";
import { formatRequestAge } from "./MessageRequestCard";
import {
  MESSAGE_REQUEST_INTENT_BY_KEY,
  MESSAGE_REQUEST_ORIGIN_BY_KEY,
} from "../../services/messageRequestApi";

// RequestDetailPane — the right pane of the two-pane "Requests" gate (design
// frame 1f). A card clicked in MessageRequestsPanel opens here: the sender as a
// large identity block, the intent/origin pills, the full first message and the
// two decisive actions with their verbatim consequence footnotes.
//
// Accept / Ignore go through the SAME flows the list cards use — the panel owns
// the optimistic list mutation + the "Accepted" banner; this pane just invokes
// them (via onAccept/onIgnore passed down from the inbox) and then closes.
//
// Props:
//   request      the selected MessageRequestReceived row.
//   onAccept     (id) => Promise<{success, channel_cid}>  — panel's accept flow.
//   onIgnore     (id) => Promise                          — panel's ignore flow.
//   onOpenThread (cid) => void  — deep-link into the freshly created chat.
//   onClose      () => void      — clear the selection (back to the prompt).
//   t            i18n passthrough (identity default = verbatim copy).
const RequestDetailPane = ({
  request,
  onAccept,
  onIgnore,
  onOpenThread,
  onClose,
  t = (s) => s,
}) => {
  const [busy, setBusy] = useState(false);

  if (!request) return null;

  const sender = request.sender || {};
  const name = sender.name || t("Agent");
  const agency = sender.agency || "";
  const city = sender.city || "";
  const first = String(name).trim().split(/\s+/)[0] || name;

  const intent = MESSAGE_REQUEST_INTENT_BY_KEY[request.intent];
  const origin = MESSAGE_REQUEST_ORIGIN_BY_KEY[request.origin];
  const manualText =
    request.intent === "manual" ? request.intent_manual_text : "";
  const intentLabel = manualText
    ? `${intent?.label || t("Manual")}: "${manualText}"`
    : intent?.label;

  // D-A (Marin 2026-08-04): the pane used to print "Licensed agent"
  // UNCONDITIONALLY — a credential claim the snapshot payload
  // (sender_name/agency only, Invariant 9) cannot back. Even under the
  // badge rule (self-declaration carries the badge) this payload ships
  // neither license_number nor license_verification_status, so no claim
  // can render here; the subtitle keeps the neutral facts.
  const subtitle = [agency, city].filter(Boolean).join(" · ");

  const doAccept = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await onAccept?.(request.id);
      if (res?.channel_cid) onOpenThread?.(res.channel_cid);
    } finally {
      setBusy(false);
      onClose?.();
    }
  };

  const doIgnore = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onIgnore?.(request.id);
    } finally {
      setBusy(false);
      onClose?.();
    }
  };

  return (
    <div className="flex h-full w-full flex-col overflow-y-auto bg-white">
      <div className="mx-auto w-full max-w-[560px] px-6 py-8">
        {/* Identity block */}
        <div className="flex items-start gap-4">
          <Avatar
            src={sender.profile_picture_url}
            alt={name}
            fallbackText={name}
            size={56}
          />
          <div className="flex min-w-0 flex-1 flex-col" style={{ gap: 3 }}>
            <span className="truncate text-[18px] font-bold text-[#101828]" title={name}>
              {name}
            </span>
            <span className="text-[12.5px] text-[#667085]">{subtitle}</span>
            <span className="text-[11.5px] text-[#98A2B3]">
              {t("Received")} {formatRequestAge(request.created_at)}
            </span>
          </div>
        </div>

        {/* Pills */}
        <div className="mt-4 flex flex-wrap items-center gap-1.5">
          <span
            className="rounded-[6px] font-semibold"
            style={{
              padding: "2px 8px",
              background: "#F0F6FF",
              border: "1px solid #D6E6FF",
              color: "#1466D8",
              fontSize: 11,
            }}
          >
            {t("Agent")}
          </span>
          {intentLabel && (
            <span
              className="rounded-full font-semibold"
              style={{
                padding: "2px 10px",
                background: intent?.pillBg || "#F2F4F7",
                color: intent?.pillText || "#475467",
                fontSize: 11,
              }}
            >
              {intentLabel}
            </span>
          )}
          {origin && (
            <span
              className="rounded-[6px] font-medium"
              style={{
                padding: "2px 8px",
                background: "#FFFFFF",
                border: "1px solid #E6EAF0",
                color: "#667085",
                fontSize: 11,
              }}
            >
              {origin.tagLabel}
            </span>
          )}
        </div>

        {/* First message */}
        <div
          className="mt-4 rounded-[12px] leading-[1.55] text-[#344054]"
          style={{
            padding: "14px 16px",
            background: "#F8FAFC",
            border: "1px solid #EDF0F4",
            fontSize: 13.5,
            whiteSpace: "pre-wrap",
          }}
        >
          {request.message}
        </div>

        {/* Actions */}
        <div className="mt-5 flex gap-3">
          <button
            type="button"
            onClick={doAccept}
            disabled={busy}
            className="flex-1 rounded-[10px] text-center text-[14px] font-semibold text-white disabled:opacity-60"
            style={{ padding: "12px 0", background: "#2283FF" }}
          >
            {busy ? t("Working…") : t("Accept")}
          </button>
          <button
            type="button"
            onClick={doIgnore}
            disabled={busy}
            className="flex-1 rounded-[10px] text-center text-[14px] font-semibold text-[#475467] disabled:opacity-60"
            style={{ padding: "12px 0", background: "#FFFFFF", border: "1px solid #E4E9F0" }}
          >
            {t("Ignore")}
          </button>
        </div>

        {/* Verbatim consequence footnotes (design frame 1f). */}
        <div className="mt-4 flex flex-col gap-2 text-[11.5px] leading-[1.5] text-[#98A2B3]">
          <p>
            <span className="font-semibold text-[#475467]">{t("Accept")}</span>{" "}
            {t("opens a 14-day conversation in your Last Minute tab — you hold the timer.")}
          </p>
          <p>
            <span className="font-semibold text-[#475467]">{t("Ignore")}</span>{" "}
            {t("quietly removes this request.")} {first}{" "}
            {t("is never notified and sees ‘pending’ forever.")}
          </p>
        </div>
      </div>
    </div>
  );
};

export default RequestDetailPane;
